/**
 * Results table for the /reranker tool: one row per scored book with the
 * score, the signal that carried it, the feature breakdown and the pipeline
 * flags. Rows the warm pipeline would exclude are greyed, not hidden.
 */
import { BookOpen } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../i18n/format';
import type { AffinityBookRow, AffinityScoreType } from '../services/api';
import AffinityReasons from './AffinityReasons';
import { coverUrl, FEATURE_ORDER, FLAG_STYLES, scoreBarScale } from './affinityPresentation';

function Cover({ row }: Readonly<{ row: AffinityBookRow }>) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className="w-10 h-14 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
        <BookOpen size={16} className="text-gray-400" />
      </div>
    );
  }
  return (
    <img
      src={coverUrl(row)}
      alt=""
      className="w-10 h-14 rounded object-cover flex-shrink-0 shadow-sm"
      onError={() => setFailed(true)}
    />
  );
}

function ScoreBar({ score, pct }: Readonly<{ score: number; pct: number }>) {
  return (
    <div className="flex items-center gap-2 min-w-[7rem]">
      <div className="h-2 flex-1 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full bg-indigo-brand-text rounded-full" style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums text-sm font-medium text-gray-900 w-12 text-right">
        {formatNumber(score)}
      </span>
    </div>
  );
}

function FeatureCells({ features }: Readonly<{ features: Record<string, number | null> }>) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500">
      {FEATURE_ORDER.filter((key) => features[key] !== null && features[key] !== undefined).map(
        (key) => (
          <span key={key} className="whitespace-nowrap">
            <span className="text-gray-400">{t(`affinity.features.${key}`)}</span>{' '}
            <span className="tabular-nums text-gray-700">
              {formatNumber(features[key] as number)}
            </span>
          </span>
        )
      )}
    </div>
  );
}

interface AffinityTableProps {
  rows: AffinityBookRow[];
  scoreType?: AffinityScoreType;
}

export default function AffinityTable({ rows, scoreType }: Readonly<AffinityTableProps>) {
  const { t } = useTranslation();
  const barPercent = useMemo(() => scoreBarScale(rows, scoreType), [rows, scoreType]);
  const isRank = scoreType === 'rank';
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 py-8 text-center">{t('affinity.results.empty')}</p>;
  }
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2 text-left w-10">#</th>
            <th className="px-3 py-2 text-left">{t('affinity.results.book')}</th>
            <th
              className="px-3 py-2 text-left"
              title={isRank ? t('affinity.results.scoreRankHint') : undefined}
            >
              {t(isRank ? 'affinity.results.scoreRank' : 'affinity.results.score')}
            </th>
            <th className="px-3 py-2 text-left">{t('affinity.results.signal')}</th>
            <th className="px-3 py-2 text-left">{t('affinity.results.flags')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.book_id} className={row.excluded_by_pipeline ? 'opacity-50' : ''}>
              <td className="px-3 py-2 align-top tabular-nums text-gray-500">{row.rank + 1}</td>
              <td className="px-3 py-2 align-top">
                <div className="flex gap-3">
                  <Cover row={row} />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate max-w-md">
                      {row.title || row.book_id}
                    </p>
                    <p className="text-gray-500 truncate max-w-md">{row.author || '—'}</p>
                    <p className="text-xs text-gray-400 font-mono">
                      {row.book_id}
                      {row.age_group ? ` · ${row.age_group}` : ''}
                      {row.primary_subject ? ` · ${row.primary_subject}` : ''}
                    </p>
                    <FeatureCells features={row.features} />
                  </div>
                </div>
              </td>
              <td className="px-3 py-2 align-top">
                <ScoreBar score={row.score} pct={barPercent(row.score)} />
              </td>
              <td className="px-3 py-2 align-top text-gray-700">
                {t(`affinity.signals.${row.winning_signal}`)}
              </td>
              <td className="px-3 py-2 align-top">
                <AffinityReasons row={row} />
                <div className="mt-1 flex flex-wrap gap-1">
                  {row.flags.map((flag) => (
                    <span
                      key={flag}
                      className={`inline-block rounded-full border px-2 py-0.5 text-xs ${FLAG_STYLES[flag]}`}
                      title={t(`affinity.flagHints.${flag}`)}
                    >
                      {t(`affinity.flags.${flag}`)}
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
